import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    CheckCircle, XCircle, Clock, MessageSquare, AlertTriangle,
    ChevronDown, ChevronUp,
    Building2, User, Calendar, FileText, Plus, Trash2, Send, Loader2
} from 'lucide-react';
import { portalService, PortalReviewData, TimestampNote, SubmitFeedbackData } from '../../services/portal.service';

const BrandPortalPage: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const [reviewData, setReviewData] = useState<PortalReviewData | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Feedback form state
    const [decision, setDecision] = useState<SubmitFeedbackData['decision'] | ''>('');
    const [overallComments, setOverallComments] = useState('');
    const [timestampNotes, setTimestampNotes] = useState<TimestampNote[]>([]);
    const [changeRequests, setChangeRequests] = useState<Array<{
        title: string;
        description: string;
        priority: 'low' | 'medium' | 'high' | 'critical';
        category: 'content' | 'timing' | 'visual' | 'audio' | 'branding' | 'compliance' | 'other';
    }>>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    // Video player state
    const [, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState('0:00');

    // UI state
    const [showChangeRequests, setShowChangeRequests] = useState(false);
    const [showPreviousFeedback, setShowPreviousFeedback] = useState(false);

    useEffect(() => {
        if (token) {
            loadReviewData();
        }
    }, [token]);

    const loadReviewData = async () => {
        try {
            setIsLoading(true);
            setError(null);
            const data = await portalService.getReview(token!);
            setReviewData(data);
        } catch (err: any) {
            console.error('Failed to load review:', err);
            if (err.response?.status === 404) {
                setError('This review link is invalid or has expired.');
            } else if (err.response?.status === 410) {
                setError('This review link has expired. Please request a new link from the creator.');
            } else {
                setError('Unable to load review. Please try again later.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleAddTimestampNote = () => {
        setTimestampNotes([
            ...timestampNotes,
            { timestamp: currentTime, note: '', priority: 'medium' }
        ]);
    };

    const handleRemoveTimestampNote = (index: number) => {
        setTimestampNotes(timestampNotes.filter((_, i) => i !== index));
    };

    const handleUpdateTimestampNote = (index: number, field: keyof TimestampNote, value: string) => {
        const updated = [...timestampNotes];
        (updated[index] as any)[field] = value;
        setTimestampNotes(updated);
    };

    const handleAddChangeRequest = () => {
        setChangeRequests([
            ...changeRequests,
            { title: '', description: '', priority: 'medium', category: 'content' }
        ]);
        setShowChangeRequests(true);
    };

    const handleRemoveChangeRequest = (index: number) => {
        setChangeRequests(changeRequests.filter((_, i) => i !== index));
    };

    const handleUpdateChangeRequest = (index: number, field: string, value: string) => {
        const updated = [...changeRequests];
        (updated[index] as any)[field] = value;
        setChangeRequests(updated);
    };

    const handleSubmit = async () => {
        if (!decision) {
            alert('Please select a decision');
            return;
        }

        setIsSubmitting(true);
        try {
            const feedbackData: any = {
                decision,
                timestampFeedback: timestampNotes.filter(n => n.note.trim()),
                changeRequests: changeRequests.filter(cr => cr.title.trim() && cr.description.trim()),
            };
            if (overallComments) {
                feedbackData.overallComments = overallComments;
            }

            await portalService.submitFeedback(token!, feedbackData);
            setIsSubmitted(true);
        } catch (err) {
            console.error('Failed to submit feedback:', err);
            alert('Failed to submit feedback. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Loading state
    if (isLoading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
                    <p className="text-gray-400">Loading review...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
                <div className="bg-gray-800 rounded-2xl p-8 max-w-md text-center border border-gray-700">
                    <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <AlertTriangle className="w-8 h-8 text-red-400" />
                    </div>
                    <h1 className="text-xl font-bold text-white mb-2">Unable to Access Review</h1>
                    <p className="text-gray-400">{error}</p>
                </div>
            </div>
        );
    }

    // Success state
    if (isSubmitted) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
                <div className="bg-gray-800 rounded-2xl p-8 max-w-md text-center border border-gray-700">
                    <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle className="w-8 h-8 text-green-400" />
                    </div>
                    <h1 className="text-xl font-bold text-white mb-2">Feedback Submitted!</h1>
                    <p className="text-gray-400 mb-4">
                        Thank you for your feedback. The creator has been notified and will review your comments.
                    </p>
                    <p className="text-sm text-gray-500">
                        You can close this page now.
                    </p>
                </div>
            </div>
        );
    }

    if (!reviewData) return null;

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
            {/* Header */}
            <header className="bg-gray-800/80 backdrop-blur-lg border-b border-gray-700 sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 py-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-xl font-bold text-white">{reviewData.title}</h1>
                            <p className="text-sm text-gray-400">
                                Version {reviewData.versionNumber} • Round {reviewData.currentRound}
                            </p>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="text-right">
                                <p className="text-sm text-gray-400">Review requested by</p>
                                <p className="text-white font-medium">{reviewData.submittedBy?.name || 'Creator'}</p>
                            </div>
                            <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center">
                                <User className="w-5 h-5 text-white" />
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-4 py-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Main Content - Preview */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Video/Content Preview */}
                        <div className="bg-gray-800 rounded-2xl overflow-hidden border border-gray-700">
                            {reviewData.previewUrl ? (
                                <div className="relative aspect-video bg-black">
                                    {reviewData.previewType === 'video' ? (
                                        <video
                                            src={reviewData.previewUrl}
                                            className="w-full h-full object-contain"
                                            controls
                                            onTimeUpdate={(e) => {
                                                const video = e.target as HTMLVideoElement;
                                                const mins = Math.floor(video.currentTime / 60);
                                                const secs = Math.floor(video.currentTime % 60);
                                                setCurrentTime(`${mins}:${secs.toString().padStart(2, '0')}`);
                                            }}
                                            onPlay={() => setIsPlaying(true)}
                                            onPause={() => setIsPlaying(false)}
                                        />
                                    ) : (
                                        <img
                                            src={reviewData.previewUrl}
                                            alt={reviewData.title}
                                            className="w-full h-full object-contain"
                                        />
                                    )}
                                </div>
                            ) : (
                                <div className="aspect-video bg-gray-900 flex items-center justify-center">
                                    <div className="text-center">
                                        <FileText className="w-12 h-12 text-gray-600 mx-auto mb-2" />
                                        <p className="text-gray-500">No preview available</p>
                                    </div>
                                </div>
                            )}

                            {/* Timestamp Notes Button */}
                            {reviewData.previewType === 'video' && (
                                <div className="p-4 border-t border-gray-700">
                                    <button
                                        onClick={handleAddTimestampNote}
                                        className="flex items-center gap-2 px-4 py-2 bg-primary-600/20 text-primary-400 rounded-lg hover:bg-primary-600/30 transition-colors"
                                    >
                                        <Plus className="w-4 h-4" />
                                        Add Note at {currentTime}
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Timestamp Notes */}
                        {timestampNotes.length > 0 && (
                            <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
                                <h3 className="text-lg font-semibold text-white mb-4">Timestamp Notes</h3>
                                <div className="space-y-4">
                                    {timestampNotes.map((note, idx) => (
                                        <div key={idx} className="flex items-start gap-4 p-4 bg-gray-700/50 rounded-xl">
                                            <div className="bg-primary-600 px-3 py-1 rounded-lg text-white text-sm font-mono">
                                                {note.timestamp}
                                            </div>
                                            <div className="flex-1 space-y-2">
                                                <textarea
                                                    value={note.note}
                                                    onChange={(e) => handleUpdateTimestampNote(idx, 'note', e.target.value)}
                                                    placeholder="Add your feedback for this timestamp..."
                                                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 resize-none"
                                                    rows={2}
                                                />
                                                <select
                                                    value={note.priority}
                                                    onChange={(e) => handleUpdateTimestampNote(idx, 'priority', e.target.value)}
                                                    className="px-3 py-1 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm"
                                                >
                                                    <option value="low">Low Priority</option>
                                                    <option value="medium">Medium Priority</option>
                                                    <option value="high">High Priority</option>
                                                </select>
                                            </div>
                                            <button
                                                onClick={() => handleRemoveTimestampNote(idx)}
                                                className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Description */}
                        {reviewData.description && (
                            <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
                                <h3 className="text-lg font-semibold text-white mb-3">Description</h3>
                                <p className="text-gray-300">{reviewData.description}</p>
                            </div>
                        )}

                        {/* Previous Feedback */}
                        {reviewData.previousFeedback && reviewData.previousFeedback.length > 0 && (
                            <div className="bg-gray-800 rounded-2xl border border-gray-700 overflow-hidden">
                                <button
                                    onClick={() => setShowPreviousFeedback(!showPreviousFeedback)}
                                    className="w-full flex items-center justify-between p-6 text-left"
                                >
                                    <h3 className="text-lg font-semibold text-white">
                                        Previous Feedback ({reviewData.previousFeedback.length})
                                    </h3>
                                    {showPreviousFeedback ? (
                                        <ChevronUp className="w-5 h-5 text-gray-400" />
                                    ) : (
                                        <ChevronDown className="w-5 h-5 text-gray-400" />
                                    )}
                                </button>
                                {showPreviousFeedback && (
                                    <div className="p-6 pt-0 space-y-4">
                                        {reviewData.previousFeedback.map((fb, idx) => (
                                            <div key={idx} className="p-4 bg-gray-700/50 rounded-xl">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <span className={`px-2 py-1 text-xs rounded-full ${fb.decision === 'approved' ? 'bg-green-500/20 text-green-400' :
                                                        fb.decision === 'rejected' ? 'bg-red-500/20 text-red-400' :
                                                            'bg-yellow-500/20 text-yellow-400'
                                                        }`}>
                                                        {fb.decision.replace(/_/g, ' ')}
                                                    </span>
                                                    <span className="text-sm text-gray-500">
                                                        {new Date(fb.submittedAt).toLocaleDateString()}
                                                    </span>
                                                </div>
                                                {fb.overallComments && (
                                                    <p className="text-gray-300 text-sm">{fb.overallComments}</p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Sidebar - Feedback Form */}
                    <div className="space-y-6">
                        {/* Review Info */}
                        <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
                            <h3 className="text-lg font-semibold text-white mb-4">Review Details</h3>
                            <div className="space-y-3 text-sm">
                                <div className="flex items-center gap-3">
                                    <Building2 className="w-4 h-4 text-gray-400" />
                                    <span className="text-gray-300">{reviewData.brandContact.company}</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <User className="w-4 h-4 text-gray-400" />
                                    <span className="text-gray-300">{reviewData.brandContact.name}</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <Calendar className="w-4 h-4 text-gray-400" />
                                    <span className="text-gray-300">
                                        Submitted {new Date(reviewData.submittedAt).toLocaleDateString()}
                                    </span>
                                </div>
                                {reviewData.reviewDueAt && (
                                    <div className="flex items-center gap-3">
                                        <Clock className="w-4 h-4 text-yellow-400" />
                                        <span className="text-yellow-400">
                                            Due {new Date(reviewData.reviewDueAt).toLocaleDateString()}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Decision Buttons */}
                        <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
                            <h3 className="text-lg font-semibold text-white mb-4">Your Decision</h3>
                            <div className="space-y-3">
                                <button
                                    onClick={() => setDecision('approved')}
                                    className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${decision === 'approved'
                                        ? 'border-green-500 bg-green-500/20'
                                        : 'border-gray-600 hover:border-green-500/50'
                                        }`}
                                >
                                    <CheckCircle className={`w-5 h-5 ${decision === 'approved' ? 'text-green-400' : 'text-gray-400'}`} />
                                    <span className={decision === 'approved' ? 'text-green-400' : 'text-gray-300'}>
                                        Approve
                                    </span>
                                </button>
                                <button
                                    onClick={() => setDecision('approved_with_changes')}
                                    className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${decision === 'approved_with_changes'
                                        ? 'border-yellow-500 bg-yellow-500/20'
                                        : 'border-gray-600 hover:border-yellow-500/50'
                                        }`}
                                >
                                    <MessageSquare className={`w-5 h-5 ${decision === 'approved_with_changes' ? 'text-yellow-400' : 'text-gray-400'}`} />
                                    <span className={decision === 'approved_with_changes' ? 'text-yellow-400' : 'text-gray-300'}>
                                        Approve with Changes
                                    </span>
                                </button>
                                <button
                                    onClick={() => setDecision('needs_discussion')}
                                    className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${decision === 'needs_discussion'
                                        ? 'border-blue-500 bg-blue-500/20'
                                        : 'border-gray-600 hover:border-blue-500/50'
                                        }`}
                                >
                                    <Clock className={`w-5 h-5 ${decision === 'needs_discussion' ? 'text-blue-400' : 'text-gray-400'}`} />
                                    <span className={decision === 'needs_discussion' ? 'text-blue-400' : 'text-gray-300'}>
                                        Needs Discussion
                                    </span>
                                </button>
                                <button
                                    onClick={() => setDecision('rejected')}
                                    className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${decision === 'rejected'
                                        ? 'border-red-500 bg-red-500/20'
                                        : 'border-gray-600 hover:border-red-500/50'
                                        }`}
                                >
                                    <XCircle className={`w-5 h-5 ${decision === 'rejected' ? 'text-red-400' : 'text-gray-400'}`} />
                                    <span className={decision === 'rejected' ? 'text-red-400' : 'text-gray-300'}>
                                        Reject
                                    </span>
                                </button>
                            </div>
                        </div>

                        {/* Comments */}
                        <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
                            <h3 className="text-lg font-semibold text-white mb-4">Overall Comments</h3>
                            <textarea
                                value={overallComments}
                                onChange={(e) => setOverallComments(e.target.value)}
                                placeholder="Share your overall thoughts and feedback..."
                                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white placeholder-gray-500 resize-none"
                                rows={4}
                            />
                        </div>

                        {/* Change Requests */}
                        <div className="bg-gray-800 rounded-2xl border border-gray-700 overflow-hidden">
                            <button
                                onClick={() => setShowChangeRequests(!showChangeRequests)}
                                className="w-full flex items-center justify-between p-6 text-left"
                            >
                                <h3 className="text-lg font-semibold text-white">
                                    Change Requests ({changeRequests.length})
                                </h3>
                                {showChangeRequests ? (
                                    <ChevronUp className="w-5 h-5 text-gray-400" />
                                ) : (
                                    <ChevronDown className="w-5 h-5 text-gray-400" />
                                )}
                            </button>
                            {showChangeRequests && (
                                <div className="p-6 pt-0 space-y-4">
                                    {changeRequests.map((cr, idx) => (
                                        <div key={idx} className="p-4 bg-gray-700/50 rounded-xl space-y-3">
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm text-gray-400">Change Request #{idx + 1}</span>
                                                <button
                                                    onClick={() => handleRemoveChangeRequest(idx)}
                                                    className="p-1 text-gray-400 hover:text-red-400"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                            <input
                                                type="text"
                                                value={cr.title}
                                                onChange={(e) => handleUpdateChangeRequest(idx, 'title', e.target.value)}
                                                placeholder="Title"
                                                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white"
                                            />
                                            <textarea
                                                value={cr.description}
                                                onChange={(e) => handleUpdateChangeRequest(idx, 'description', e.target.value)}
                                                placeholder="Description"
                                                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white resize-none"
                                                rows={2}
                                            />
                                            <div className="grid grid-cols-2 gap-2">
                                                <select
                                                    value={cr.priority}
                                                    onChange={(e) => handleUpdateChangeRequest(idx, 'priority', e.target.value)}
                                                    className="px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm"
                                                >
                                                    <option value="low">Low</option>
                                                    <option value="medium">Medium</option>
                                                    <option value="high">High</option>
                                                    <option value="critical">Critical</option>
                                                </select>
                                                <select
                                                    value={cr.category}
                                                    onChange={(e) => handleUpdateChangeRequest(idx, 'category', e.target.value)}
                                                    className="px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm"
                                                >
                                                    <option value="content">Content</option>
                                                    <option value="visual">Visual</option>
                                                    <option value="audio">Audio</option>
                                                    <option value="timing">Timing</option>
                                                    <option value="branding">Branding</option>
                                                    <option value="compliance">Compliance</option>
                                                    <option value="other">Other</option>
                                                </select>
                                            </div>
                                        </div>
                                    ))}
                                    <button
                                        onClick={handleAddChangeRequest}
                                        className="w-full flex items-center justify-center gap-2 p-3 border-2 border-dashed border-gray-600 rounded-xl text-gray-400 hover:border-primary-500 hover:text-primary-400 transition-colors"
                                    >
                                        <Plus className="w-4 h-4" />
                                        Add Change Request
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Submit Button */}
                        <button
                            onClick={handleSubmit}
                            disabled={!decision || isSubmitting}
                            className="w-full flex items-center justify-center gap-2 py-4 bg-gradient-to-r from-primary-600 to-indigo-600 text-white rounded-xl font-semibold hover:from-primary-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        >
                            {isSubmitting ? (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    Submitting...
                                </>
                            ) : (
                                <>
                                    <Send className="w-5 h-5" />
                                    Submit Feedback
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <footer className="border-t border-gray-700 py-6 mt-12">
                <div className="max-w-7xl mx-auto px-4 text-center text-sm text-gray-500">
                    <p>Powered by CreatorHub • Secure brand review portal</p>
                </div>
            </footer>
        </div>
    );
};

export default BrandPortalPage;
